import { iconsMap } from "@/public/icons/iconsMap"
import styled from "@emotion/styled"
import {
  Box,
  CircularProgress,
  FormHelperText,
  Typography,
} from "@mui/material"
import { FormFieldDto } from "api"
import axios from "axios"
import { ChangeEvent, useState } from "react"
import { FieldError, UseFormSetValue } from "react-hook-form"
import { colors, mediaQueries } from "ui"
import { FormFieldSettingsProps } from "../utils/interfaces"

const StyledUplaodWrapper = styled.div<{ status: boolean }>`
  display: flex;
  position: relative;
  overflow: hidden;
  display: inline-block;
  border-radius: 4px;
  background-color: #fff;
  justify-content: flex-start;

  flex-wrap: wrap;
  height: 100%;
  width: 100%;
  cursor: pointer;
  input {
    font-size: 100px;
    position: absolute;
    left: 0;
    top: 0;
    opacity: 0;
  }
`

const StyledFileName = styled.div`
  width: 200px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  ${mediaQueries.desktopM} {
    width: 150px;
  }
`

const StyledUploadLink = styled.label`
  display: flex;
  cursor: pointer;
  align-items: center;
  font-size: 18px;
  color: ${colors?.black};
  text-decoration: underline;
  width: 100%;
  justify-content: flex-start;
  padding-bottom: 16px;

  ${mediaQueries.desktopM} {
    font-size: 24px;
  }

  ${mediaQueries.desktopXl} {
    font-size: 16px;
  }
`

const StyledUploadIcon = styled(Box)`
  display: inline-flex;
  align-items: center;
  margin-right: 8px;
`
const StyledUploadTexts = styled(Box)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  gap: 8px;
  text-decoration: none;
  p {
    text-decoration: none;
  }
`

const StyledDiv = styled.div`
  width: 100%;

  position: relative;
  ${mediaQueries.tabletDown} {
    position: relative;
  }
`

const StyledHelperText = styled(FormHelperText, {
  shouldForwardProp: (prop) => prop !== "status",
})<{ status: boolean }>`
  position: relative;
  bottom: unset;
  color: ${(props) => (props.status ? "#C92418" : "#008015")};
  font-size: 12px;
  line-height: 1.1;
`

const StyledTextResponse = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
`

const StyledLoaderWrapper = styled(Box)`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${colors?.black};
`

const CheckMarkErrorIcon: React.FC = iconsMap["checkMarkError"]
const CheckMarkSuccessIcon: React.FC = iconsMap["checkMarkSuccess"]

interface Props {
  setValue: UseFormSetValue<any>
  formError?: FieldError
  isSubmitted?: boolean
  onChange: (value: string[]) => void
  helpText: string
  caption?: string
  field: FormFieldDto
  clientId?: string
}

interface UploadDocument {
  DocumentName: string
  DocumentData: string
  DocumentExtension: string
  DocumentMetadata: string
}

interface UploadPayload {
  DocumentsMetadata: string
  SendToClickins: boolean
  ClientType: number
  Documents: UploadDocument[]
}

// Upload single base64 file to custom API and return file ID
const uploadBase64FileAndGetId = async (
  base64File: string,
  fileName: string,
  clientId: string,
): Promise<string | null> => {
  try {
    const extension = fileName.split(".").pop() || ""

    // Strip the data URL prefix to get clean base64 string
    const base64Data = base64File.includes(",")
      ? base64File.split(",")[1]
      : base64File

    const document: UploadDocument = {
      DocumentName: fileName,
      DocumentData: base64Data,
      DocumentExtension: extension,
      DocumentMetadata: "",
    }

    const payload: UploadPayload = {
      DocumentsMetadata: clientId,
      SendToClickins: true,
      ClientType: 9,
      Documents: [document],
    }

    const response = await axios.post("/api/files/upload-base64", payload, {
      headers: {
        "Content-Type": "application/json",
      },
    })
    return response.data?.ids?.[0] || response.data?.id || null
  } catch (error) {
    console.error("Error uploading base64 file:", error)

    if (axios.isAxiosError(error)) {
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error)
      } else if (error.response?.status === 413) {
        throw new Error("File is too large. Please select a smaller file.")
      } else if (error.response?.status === 400) {
        throw new Error(
          "Invalid file format. Please check the file and try again.",
        )
      } else if (error.response && error.response.status >= 500) {
        throw new Error("Server error. Please try again later.")
      } else {
        throw new Error(
          `Upload failed: ${error.response?.statusText || error.message}`,
        )
      }
    }

    throw new Error("An unexpected error occurred during upload.")
  }
}

const FileUpload = ({
  setValue,
  formError,
  onChange,
  helpText,
  caption,
  field,
  clientId = "",
}: Props) => {
  const settings = field?.settings as unknown as FormFieldSettingsProps
  const [fileNames, setFileNames] = useState<string[]>([])
  const [fileError, setFileError] = useState<string>("")
  const [isUploading, setIsUploading] = useState<boolean>(false)
  const UploadIcon = iconsMap["uploadIcon"]

  const handleFileInputChange = async (
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    const selectedFiles = Array.from(event.target.files || [])
    if (selectedFiles.length && field?.fileUploadOptions) {
      setIsUploading(true)
      const { allowedUploadExtensions, allowAllUploadExtensions } =
        field.fileUploadOptions
      const maxSizeInBytes = 5 * 1024 * 1024 // 5MB

      const validFiles = selectedFiles.filter((file) => {
        const fileExtension = file.name.split(".").pop()?.toLowerCase()
        const fileSize = file.size

        if (
          (!allowAllUploadExtensions &&
            !allowedUploadExtensions.includes(fileExtension || "")) ||
          fileSize > maxSizeInBytes
        ) {
          setFileError(field?.patternInvalidErrorMessage || "Error adding file")
          return false
        }
        return true
      })

      if (validFiles.length) {
        setFileError("")
        setFileNames(validFiles.map((file) => file.name))
        setValue(
          `${field.id}_filenames`,
          validFiles.map((file) => file.name),
          { shouldValidate: true },
        )

        const base64Promises = validFiles.map((file) => {
          return new Promise<string>((resolve, reject) => {
            const reader = new FileReader()
            reader.onload = () => resolve(reader.result as string)
            reader.onerror = reject
            reader.readAsDataURL(file)
          })
        })

        try {
          const base64Files = await Promise.all(base64Promises)

          if (field.alias === "document2") {
            const fileIds = await Promise.all(
              base64Files.map((base64File, idx) =>
                uploadBase64FileAndGetId(
                  base64File,
                  validFiles[idx].name,
                  clientId,
                ),
              ),
            )
            onChange(fileIds.filter((id) => id !== null) as string[])
          } else {
            // Strip data URL prefix for clean base64 strings
            const cleanBase64Files = base64Files.map((base64File) =>
              base64File.includes(",") ? base64File.split(",")[1] : base64File,
            )
            onChange(cleanBase64Files)
          }
        } catch (error) {
          console.error("Error converting files to base64:", error)
          setFileError("Error processing files")
        } finally {
          setIsUploading(false)
        }
      } else {
        setValue(`${field.id}_filenames`, [], { shouldValidate: true })
        setIsUploading(false)
      }
    } else {
      setIsUploading(false)
    }
  }
  const error = !!fileError || !!formError?.message
  return (
    <StyledDiv>
      <StyledUplaodWrapper status={error}>
        <StyledUploadLink
          className="upload-link"
          aria-label={helpText}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault()
              e.currentTarget.click()
            }
          }}
        >
          {isUploading ? (
            <StyledLoaderWrapper>
              <CircularProgress size={20} />
            </StyledLoaderWrapper>
          ) : fileNames.length ? (
            <>
              <StyledFileName className="file-name">
                {fileNames.join(", ")}
              </StyledFileName>
            </>
          ) : (
            <>
              <StyledUploadIcon className="upload-icon">
                <UploadIcon />
              </StyledUploadIcon>
              <StyledUploadTexts>
                <Typography variant="h4" component={"p"}>
                  {caption}
                </Typography>
                {helpText}
              </StyledUploadTexts>
              {field.required && " *"}
            </>
          )}
          <input
            type="file"
            onChange={handleFileInputChange}
            accept={
              field?.fileUploadOptions?.allowAllUploadExtensions
                ? "*"
                : field?.fileUploadOptions?.allowedUploadExtensions
                    .map((ext) => `.${ext}`)
                    .join(", ")
            }
            multiple={field?.fileUploadOptions?.allowMultipleFileUploads}
            aria-hidden="true"
            tabIndex={-1}
            disabled={isUploading}
          />
        </StyledUploadLink>
      </StyledUplaodWrapper>

      <Typography component={"p"} variant="body1">
        {settings.selectedFilesListHeading}
      </Typography>
      <StyledHelperText status={error}>
        {error ? (
          <StyledTextResponse>
            <CheckMarkErrorIcon />
            <span>{error && fileError ? fileError : formError?.message}</span>
          </StyledTextResponse>
        ) : null}
      </StyledHelperText>
    </StyledDiv>
  )
}

export default FileUpload
